/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import debounce from 'lodash.debounce';
import isNil from 'lodash.isnil';
import {isPhone} from '@ion/core/src/capabilities/device';
import get from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  AccountLink,
  CRUDPermissions,
  GenericObject,
  Link,
  SSOConfiguration
} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty, isEqual, isValue} from '@ion/core/src/utils/StringUtil';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {TargetType} from '@ion/dls/src/editors/AdminGroupEditorUtils';
import EditorDialog from '@ion/dls/src/editors/EditorDialog';
import styles from '@ion/dls/src/editors/editorStyles';
import {
  getInitialCtcAdminState,
  getSSOFields,
  GroupFormFields,
  serialize,
  SerializedArgs,
  validateUsers
} from '@ion/dls/src/editors/GroupEditorUtils';
import SFTPConfigurationEditor, {SFTPEditorEnablement} from '@ion/dls/src/editors/SFTPConfigurationEditor';
import {SFTPConfigurationFields} from '@ion/dls/src/editors/SFTPConfigurationEditorUtils';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {isDeleteSubMEP} from '../../../helpers';
import AccountInfo from './AccountInfo';
import AccountSettings from './AccountSettings';
import AccountSSOConfig from './AccountSSOConfig';
import {AddAccountProps} from './AddGroupEditor';
import DeleteSSOConfiguration from './DeleteSSOConfiguration';
import {MessageEndpoint, MessageEndpointAPI} from './MessageEndpoint';
import {Entry} from './SSOApps';
import {deserializeSSOApps, setSSODomain, SSOFormFields, validateSSOApps} from './SSOEditorUtil';
import Users from './Users';

export interface AccountEditorProps extends AddAccountProps, SFTPEditorEnablement {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onSubmit?: (item: GenericObject) => void;
  group?: Account | GenericObject;
  defaultAccount?: AccountLink;
  account: Account;
  isViewDialog: boolean;
  open: boolean;
  clear?: boolean;
  loading?: boolean;
  timeZone?: string;
  includeApplications?: boolean;
  parentAccount?: Account;
  onEdit?: Function;
  isChipView?: boolean;
  displayApplicationName?: boolean;
  isLoggedinUserAccount?: boolean;
  accountsPermissions?: CRUDPermissions;
  applicationPermissions?: CRUDPermissions;
  currentUserRoles?: Link[];
  hasAutoJobs?: boolean;
  autojobsSetting?: boolean;
  allowSSOConfig?: boolean;
  allowDeleteSSOConfig?: boolean;
  allowUpdateSSOConfig?: boolean;
  ssoConfig?: SSOConfiguration;
  showSSOConfig?: boolean;
  setShowSSOConfig?: Function;
  hasCustomVisionPermission?: boolean;
  isVehicleModuleEnabled?: boolean;
};

const overrides = (theme: DLSTheme) => ({
  overrides : {
    marginTop : '16px',
    padding   : '0 16px'
  },
  switchMargin : {
    display   : 'flex',
    marginTop : '16px'
  },
  overridesSwitches : {
    padding : '10px 5px'
  },
  flexStyle : {
    display : 'flex'
  },
  leftDiv : {
    width : '92%'
  },
  tooltipDiv : {
    width       : '8%',
    paddingTop  : '44px',
    paddingLeft : '10px'
  },
  switchTooltip : {
    paddingTop : '28px'
  },
  switchContainer : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column',
    width          : '100%',
    minHeight      : '100%'
  },
  switch : {
    display                        : 'flex',
    alignItems                     : 'center',
    gap                            : 8,
    [theme.breakpoints.down('sm')] : {
      display : 'block'
    }
  },
  textTooltip : {
    paddingTop   : '7px',
    paddingRight : '3px'
  },
  formActions : {
    borderTop      : `solid 2px ${dlsColor('grey5')}`,
    display        : 'flex',
    justifyContent : 'flex-end',
    bottom         : '0px'
  },
  infoIcon : {
    margin        : '0 6px',
    display       : 'inline-block',
    verticalAlign : 'middle',
    cursor        : 'pointer'
  },
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '10px 0',
    paddingLeft   : '0px'
  },
  fieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  appStyle : {
    display   : 'flex',
    marginTop : '14px'
  },
  errorMessage : {
    ...dlsTypography('body3'),
    color       : dlsColor('caRed'),
    fontSize    : '12px !important',
    paddingLeft : '16px !important'
  },
  vehicleModule : {
    color      : dlsColor('grey2'),
    fontSize   : '14px',
    fontFamily : 'Roboto',
    fontWeight : 400
  }
});

/**
 * The Account editor with set fields and encapsulated logic for handling the form's state,
 * including validation.
 *
 * @param {boolean} open: Determines if the AccountEditor dialog should be open
 * @param {object} account: User's account object
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {Function} onSubmit: Function the be run when the form is submitted.
 *   The function is passed an Account object
 * @param {object} group: Account that may be passed to the form (like in the case of editing)
 * @param {boolean} clear: If true we clear the form state
 * @param {boolean} loading: disables any for submission while the form is busy
 * @param {string} timeZone: passed to be set as default if not form default
 */
export const AccountEditor = ({
  open,
  account,
  defaultAccount,
  onCloseDialog,
  onSubmit,
  includeApplications,
  parentAccount,
  rootAccountId,
  onEdit,
  accountsPermissions,
  applicationPermissions,
  group = {},
  ssoConfig,
  showSSOConfig,
  setShowSSOConfig,
  timeZone = null,
  clear = false,
  loading = false,
  allowSSOConfig = false,
  allowDeleteSSOConfig = false,
  allowUpdateSSOConfig = false,
  isViewDialog = false,
  isChipView = false,
  displayApplicationName = false,
  isLoggedinUserAccount = false,
  includeIonAccount = false,
  includeSFTPEditor = false,
  currentUserRoles = [],
  hasAutoJobs = false,
  autojobsSetting = false,
  hasCustomVisionPermission = false,
  isVehicleModuleEnabled = false
}: AccountEditorProps) => {
  const editorStyles                                    = useExtendStyles(styles, overrides);
  const classes                                         = editorStyles();
  const [userInfo, setUserInfo]                         = useState<any>([]);
  const [isCalampSupport, setIsCalampSupport]           = useState<boolean>(false);
  const [formState, setFormState]                       = useFormState(getInitialCtcAdminState(group, ssoConfig));
  const {hasUpdatePermission}                           = accountsPermissions || {};
  const {hasAddPermission: hasApplicationAddPermission} = applicationPermissions || {};
  const calampRootAccountId                             = useMemo(() => get('rootAccountId'), []);
  const isCalAmpRootAccount                             = entityId(group) === Number(calampRootAccountId);
  const isEditSSOMode                                   = !isObjectEmpty(ssoConfig);
  const [ssoAppInfo, setSSOAppInfo]                     = useState<Entry[]>(deserializeSSOApps(ssoConfig?.apps));
  const [isSSOConfigDeleted, setIsSSOConfigDeleted]     = useState(false);
  const [isUnsavedDeletedSSO, setIsUnsavedDeletedSSO]   = useState(false);

  const isEditMode         = !isObjectEmpty(group);
  const isViewMode         = isViewDialog || false;
  const isSameGroup        = entityId(account?.href) === group?.id;
  const disableViewEdit    = group?.id === entityId(defaultAccount);
  const hoverText          = translate('common:editors.primaryButton.disabled.tooltip');
  const messageEndpointRef = useRef<MessageEndpointAPI>();
  const isEnterprise       = formState[GroupFormFields.Enterprise]?.value;
  const savedSubscription  = group?.subscription?.title;

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  /**
   * This method return a boolean.
   * If current user role is equals to CalAmp Support, it return true otherwise false.
   */
  const checkCalampSupportRole = useCallback(function(roles: Link[]) {
    return roles.some(role => role?.title === 'CalAmp Support');
  }, []);

  useEffect(() => {
    let userRole = false;
    if (currentUserRoles?.length && isEditMode) {
      userRole = checkCalampSupportRole(currentUserRoles);
    }
    setIsCalampSupport(userRole);
  }, [currentUserRoles]);

  const dialogTitle = useMemo(() => {
    const entry = isEditMode ? (isViewDialog ? 'view' : 'edit') : 'add';
    return translate(`common:editors.shared.${entry}`, {
      entityLabel : translate('common:entityLabels.account')
    });
  }, [isEditMode, isViewDialog]);

  const handleButtonSubmit = useCallback((mode: string) => {
    const isEdit = mode === 'edit';
    onEdit(isEdit);
    handleSubmit();
  }, []);

  const onAuditClick = useCallback(() => {
    handleButtonSubmit('audit');
  }, []);

  const onEditClick = useCallback(() => {
    handleButtonSubmit('edit');
  }, []);

  const clearForm = useCallback(function() {
    setShowSSOConfig?.(false);
    setFormState({}, true);
  }, []);

  const debounceFieldChange = useCallback(debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
  }, 400), []);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    debounceFieldChange(e.target);
  }, []);

  const handleSolomonFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    const {value}      = e.target;
    setFormState({
      solomonId : {
        name  : GroupFormFields.SolomonId,
        value : value.trim()
      }
    });
  }, []);

  const handleExternalRefIdFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    const {value}      = e.target;
    setFormState({
      externalRefID : {
        name  : GroupFormFields.ExternalRefID,
        value : value.trim()
      }
    });
  }, []);

  const handleSwitchChange = useCallback(function({target}) {
    const {name, checked, required = false} = target;
    setFormState({[name]: {name, value: checked, required}});
    if (isEqual(name, GroupFormFields.Enterprise)) {
      if (!formState[GroupFormFields.ApiAccess]?.value) {
        setFormState({
          [GroupFormFields.ApiAccess] : {
            name     : GroupFormFields.ApiAccess,
            value    : '',
            required : checked
          }
        });
      }
      if (isEditMode && checked && !formState[GroupFormFields.Subscription]?.value?.title) {
        setFormState({
          [GroupFormFields.Subscription] : {
            name     : GroupFormFields.Subscription,
            value    : '',
            required : checked
          }
        });
      }
      if (!checked) {
        setFormState({
          [GroupFormFields.Subscription] : {
            name  : GroupFormFields.Subscription,
            error : checked
          },
          [GroupFormFields.Users] : {
            name  : GroupFormFields.Users,
            error : checked
          }
        });
      }
    }
    const field = GroupFormFields.AutojobsEnabled;
    if (isEqual(name, field)) {
      setFormState({
        [field] : {
          name  : field,
          value : checked
        }
      });
    }
    if (isEqual(name, GroupFormFields.VehicleModule)) {
      setFormState({
        [GroupFormFields.VehicleModule] : {
          name  : GroupFormFields.VehicleModule,
          value : checked
        }
      });
    }
  }, []);

  const handleChooserChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
    if (isEqual(name, GroupFormFields.Subscription)) {
      setFormState({
        [GroupFormFields.DoDeleteMsgEP] : {
          name  : GroupFormFields.DoDeleteMsgEP,
          value : isDeleteSubMEP(isEnterprise, savedSubscription, value?.name)
        }
      });
    }
  }, [isEnterprise, savedSubscription]);

  const handleApplicationChooserChange = useCallback(({name, value, required = false, error = false}) => {
    setFormState({[name]: {name, value: value, required, error}});
  }, []);

  const handleRadioGroupChange = useCallback((value: string, name: string) => {
    setFormState({
      [name] : {name, value}
    });
  }, []);

  /**
   * This method is use to close the Edit dialog with clear Form. It check if the
   * `isSSOConfigDeleted` is true, set the `setIsUnsavedDeletedSSO` state to true to
   * display a warning dialog stating `Unsaved deleted SSO Configuration` for Account.
   * Additionally, prevent the user from clearing and closing the Edit dialog without
   * saving the changes.
   */
  const onCancelDialog = useCallback(function(e): void {
    if (isSSOConfigDeleted) {
      setIsUnsavedDeletedSSO(true);
    } else {
      clearForm();
      onCloseDialog?.(e);
    }
  }, [onCloseDialog, isSSOConfigDeleted]);

  /**
   * This method is use to close the dialog. It check If the SSO Configuration is deleted,
   * set the `setIsUnsavedDeletedSSO` state to true to display a warning dialog stating
   * `Unsaved deleted SSO Configuration` for Account. Additionally, prevent the user from
   * closing the Edit dialog without saving the changes.
   */
  const handleCloseDialog = useCallback(function(e): void {
    if (isSSOConfigDeleted) {
      setIsUnsavedDeletedSSO(true);
    } else {
      onCloseDialog(e);
    }
  }, [onCloseDialog, isSSOConfigDeleted]);

  const submitValidation = {
    [GroupFormFields.Applications] : ({value}) => {
      return includeApplications ? value?.length : true;
    },
    [GroupFormFields.Subscription] : ({value}) => {
      const valid = value !== '' && !isNil(value);
      return isEnterprise ? valid : true;
    },
    [GroupFormFields.ApiAccess] : ({value}) => {
      const valid = !isEmpty(value);
      return isEnterprise && !isEditMode ? !hasApplicationAddPermission || valid : true;
    },
    [GroupFormFields.Users] : () => {
      if (isEnterprise && !isEditMode) {
        const {isValid, userList} = validateUsers(userInfo);
        setUserInfo(userList);
        return isValid;
      }
      return true;
    },
    [GroupFormFields.MessageEndpoint] : () => {
      return messageEndpointRef.current?.validate();
    },
    [SSOFormFields.APPs] : () => {
      if (showSSOConfig) {
        const {isValid, appList} = validateSSOApps(ssoAppInfo);
        setSSOAppInfo(appList);
        return isValid;
      }
      return true;
    },
    [SSOFormFields.Domains] : () => {
      if (showSSOConfig) {
        const domain  = formState[SSOFormFields.Domains]?.value;
        const isValid = setSSODomain(domain, setFormState);
        return !isValid;
      }
      return true;
    }
  };

  const serializer = function(data: FormState): GenericObject {
    const options = {
      data                     : data,
      includeSSOConfig         : showSSOConfig,
      includeAutojobsAttribute : hasAutoJobs && !isCalAmpRootAccount
    } as SerializedArgs;
    return serialize(options);
  };

  /**
   * Since we are using useImperativeHandler, we need to set the form state outside of the immediate
   * scope of the functional component (Account Editor). Therefore this will need to be called solely
   * when the user intends to 'save' the form, not as a part of hook initialization.
   *
   * @see https://dmitripavlutin.com/react-useref-guide/#3-updating-references-restriction
   */
  const getFormState = function(newFormState: FormState) {
    const messageState = messageEndpointRef.current?.getMessageEndpointState?.();
    return messageState ? {
      ...newFormState,
      [GroupFormFields.MessageEndpoint] : messageState
    } : newFormState;
  };

  function handleSFTPConnectionChange(selected) {
    setFormState({
      [GroupFormFields.SFTPServerId] : {
        name  : GroupFormFields.SFTPServerId,
        value : selected?.[SFTPConfigurationFields.Name] || ''
      }
    });
  }

  const handleSSOConfigToggle = function(event) {
    const checked = event.target.checked;
    setShowSSOConfig?.(checked);
    setFormState(getSSOFields(group, ssoConfig, checked));
  };

  const onCloseNotificationDialog = useCallback(() => {
    setIsUnsavedDeletedSSO(false);
  }, []);

  /**
   * When the SSO Configuration is deleted, set the `setShowSSOConfig` state to false
   * to uncheck the 'Edit SSO Configuration' checkbox. This will also hide the
   * 'Delete SSO Configuration' CTA. ...
   */
  useEffect(() => {
    if (isSSOConfigDeleted) {
      const timeoutId = setTimeout(() => {
        setShowSSOConfig?.(false);
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isSSOConfigDeleted]);

  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onSubmit,
    formState,
    getFormState,
    setFormState
  });

  if (formState[GroupFormFields.GroupName]?.error) {
    smoothScrollByElement('editor');
  } else if (formState[GroupFormFields.Applications]?.error) {
    smoothScrollByElement('editor', 'center');
  } else if (formState[SSOFormFields.DiscoveryEndpoint]?.error || formState[SSOFormFields.APPs]?.error) {
    smoothScrollByElement('editor', 'end');
  }

  return (
    <EditorDialog open={open}
      dialogTitle={dialogTitle}
      onAuditClick={onAuditClick}
      onEditClick={onEditClick}
      disableViewEdit={disableViewEdit}
      hasUpdatePermission={hasUpdatePermission}
      onCloseDialog={handleCloseDialog}
      onCancelDialog={onCancelDialog}
      onPrimaryButtonClick={handleSubmit}
      omitActions={isViewMode}
      isViewDialog={isViewDialog}
      primaryButtonLoading={loading}
      primaryButtonDisabled={isSameGroup && disableViewEdit}
      primaryButtonDisabledText={hoverText}
      isLoggedinUserAccount={isLoggedinUserAccount}>
      <form id={'editor'} className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <AccountInfo group={group}
            handleSolomonFieldChange={handleSolomonFieldChange}
            handleExternalRefIdFieldChange={handleExternalRefIdFieldChange}
            handleChooserChange={handleChooserChange}
            handleSwitchChange={handleSwitchChange}
            handleFieldChange={handleFieldChange}
            parentAccount={parentAccount}
            defaultAccount={defaultAccount}
            account={account}
            isCalampSupport={isCalampSupport}
            isSameGroup={isSameGroup}
            hasAutoJobs={hasAutoJobs}
            formState={formState}
            isViewMode={isViewMode}
            isEditMode={isEditMode}
            open={open}
            classes={classes}
            showServiceProfile={accountsPermissions?.hasSpEditPermission}
            isLoggedinUserAccount={isLoggedinUserAccount}
            isCalAmpRootAccount={isCalAmpRootAccount}
            isVehicleModuleEnabled={isVehicleModuleEnabled}
            autojobsSetting={autojobsSetting} />
          <MessageEndpoint enabled
            ref={messageEndpointRef}
            account={group}
            isView={isViewMode}
            isEdit={isEditMode}
            isLoggedinUserAccount={isLoggedinUserAccount}
            subscription={formState[GroupFormFields.Subscription]?.value} />
          <AccountSettings group={group}
            handleApplicationChooserChange={handleApplicationChooserChange}
            hasCreateAppPermission={hasApplicationAddPermission}
            hasCustomVisionPermission={hasCustomVisionPermission}
            handleRadioGroupChange={handleRadioGroupChange}
            handleChooserChange={handleChooserChange}
            handleSwitchChange={handleSwitchChange}
            handleFieldChange={handleFieldChange}
            setFormState={setFormState}
            includeApplications={includeApplications}
            includeIonAccount={includeIonAccount}
            rootAccountId={rootAccountId}
            account={account}
            formState={formState}
            timeZone={timeZone}
            isChipView={isChipView}
            isViewDialog={isViewMode}
            isEditMode={isEditMode}
            displayApplicationName={displayApplicationName}
            includeSFTPEditor={includeSFTPEditor}
            classes={classes}
            open={open}
            isLoggedinUserAccount={isLoggedinUserAccount}/>
          {(isEnterprise && !isEditMode) && (
            <Users required
              userInfo={userInfo}
              minUser={1}
              onChange={function(value) {
                setUserInfo(value);
                handleChooserChange({
                  name     : GroupFormFields.Users,
                  required : isEnterprise,
                  value    : value
                });
              }} />
          )}
          {includeSFTPEditor && (
            <SFTPConfigurationEditor account={group ? {
              ...group._self,
              title : group.name
            } : null}
            isView={isViewMode}
            showSSOConfig={allowSSOConfig && isEditMode}
            onChange={handleSFTPConnectionChange}
            defaultValue={formState[GroupFormFields.SFTPServerId].value} />
          )}
          <ConditionalRender condition={allowSSOConfig && isEditMode && !isViewMode}>
            <h1 className={classes.formSectionTitle}>{translate('common:entityLabels.ssoConfiguration')}</h1>
            <div className={classes.switch}>
              <div className={classes.overridesSwitches}>
                <CheckBox label={translate('common:editors.shared.edit', {entityLabel: translate('common:entityLabels.ssoConfiguration')})}
                  onChange={handleSSOConfigToggle}
                  disabled={(isSSOConfigDeleted && !showSSOConfig)}
                  checked={showSSOConfig} />
              </div>
              <Render.If condition={showSSOConfig && allowDeleteSSOConfig && isEditSSOMode}>
                <DeleteSSOConfiguration ssoConfig={ssoConfig}
                  setIsSSOConfigDeleted={setIsSSOConfigDeleted}
                  isSSOConfigDeleted={isSSOConfigDeleted} />
              </Render.If>
            </div>
            <AccountSSOConfig selectedAccount={group}
              isSSOConfigDeleted={isSSOConfigDeleted}
              allowEdit={showSSOConfig && !(isEditSSOMode && !allowUpdateSSOConfig)}
              isEditSSOMode={isEditSSOMode}
              ssoAppInfo={ssoAppInfo}
              setSSOAppInfo={setSSOAppInfo}
              formState={formState}
              setFormState={setFormState}
              ssoConfiguration={ssoConfig}/>
          </ConditionalRender>
          <Render.If condition={isUnsavedDeletedSSO}>
            <UserNotificationDialog show
              capitalize
              zIndex={6000}
              onAction={onCloseNotificationDialog}
              actionLabel={translate('common:ok')}
              title={translate('admin-groups:dialog.ssoConfiguration.deletedwarningTitle')}
              message={translate('admin-groups:dialog.ssoConfiguration.deletedWarningMessage')} />
          </Render.If>
        </div>
      </form>
    </EditorDialog>
  );
};

export default AccountEditor;
